import { type BrokerCommunication } from "@dune/types";
import { RoleEnum } from "@dune/vars";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useMe } from "../api/queries/users.queries";
import {
	EditableFileName,
	FileWithMetadataPreviewPanel,
	FileWithMetadataPreviewPanelActions,
} from "../component/layout/FileWithMetadataPreviewWrapper";
import { EmailViewer } from "../email/EmailViewer";
import { useDocumentStore } from "../stores/document.store";
import { useEmailActions, useEmailStore } from "../stores/email.store";
import { type PanelContent, usePanelActions } from "../stores/panel.store";
import { useTaskActions } from "../stores/task.store";
import type {
	EmailComposerPanelProps,
	TaskDetailsPanelProps,
} from "../types/panel.types";
import { getEntityPanelComponent } from "../utils/panel-registry";

export const usePanel = () => {
	const { setPanel, clearPanel, goBack, updatePanelMeta } = usePanelActions();
	const { clearEmailSelection, clearComposerState } = useEmailActions();
	const { clearTaskSelection } = useTaskActions();
	const clearDocument = useDocumentStore((state) => state.clearDocument);
	const { me } = useMe();

	const showEmailComposer = useCallback(
		(props: EmailComposerPanelProps) => {
			const EmailComposerComponent = getEntityPanelComponent(
				props.entityType,
				"EmailComposer",
			);

			if (!EmailComposerComponent) {
				const errorMessage = `No EmailComposer component registered for entity type: ${props.entityType}`;
				console.error(errorMessage);

				if (import.meta.env.DEV) {
					toast.error("Panel manquant", {
						description: `EmailComposer non configuré pour "${props.entityType}". Vérifiez l'enregistrement des panels.`,
						duration: 5000,
					});
				}
				return;
			}

			const onCloseHandler = () => {
				const isDirty = useEmailStore.getState().composerDirty;

				if (isDirty) {
					useEmailStore.getState().actions.setComposerShouldClose(true);
				} else {
					clearComposerState();
					clearPanel();
					if (props.onClose) {
						props.onClose();
					}
				}
			};

			const handleComponentClose = () => {
				clearComposerState();
				clearPanel();
				if (props.onClose) {
					props.onClose();
				}
			};

			setPanel({
				id: `email-composer-${Date.now()}`,
				component: (
					<EmailComposerComponent {...props} onClose={handleComponentClose} />
				),
				meta: {
					title: "Nouveau message",
					onClose: onCloseHandler,
				},
			});
		},
		[setPanel, clearPanel, clearComposerState],
	);

	const showEmailPreview = useCallback(
		(props: {
			mail: BrokerCommunication;
			brokerId?: number;
			onImportEmail?: (emailAttachmentId: number, brokerId: number) => void;
			actions?: React.ReactNode;
			onClose?: () => void;
		}) => {
			const onCloseHandler = () => {
				clearPanel();
				clearEmailSelection();
				props.onClose?.();
			};

			setPanel({
				id: `email-preview-${props.mail.id}`,
				component: (
					<EmailViewer
						from={props.mail.from}
						to={props.mail.to.join(", ")}
						date={props.mail.date}
						content={props.mail.htmlContent}
						brokerId={props.brokerId}
						emailId={props.mail.id.toString()}
						onImportEmail={props.onImportEmail}
						attachments={props.mail.attachments}
					/>
				),
				meta: {
					title: props.mail.subject,
					actions: props.actions,
					onClose: onCloseHandler,
					onBack: onCloseHandler,
				},
			});
		},
		[setPanel, clearPanel, clearEmailSelection],
	);

	const showTaskDetails = useCallback(
		(props: TaskDetailsPanelProps) => {
			if (me?.role !== RoleEnum.DUNE) {
				return;
			}

			const TaskDetailsComponent = getEntityPanelComponent(
				props.entityType,
				"TaskDetails",
			);

			if (!TaskDetailsComponent) {
				const errorMessage = `No TaskDetails component registered for entity type: ${props.entityType}`;
				console.error(errorMessage);

				if (import.meta.env.DEV) {
					toast.error("Panel manquant", {
						description: `TaskDetails non configuré pour "${props.entityType}". Vérifiez l'enregistrement des panels.`,
						duration: 5000,
					});
				}
				return;
			}

			const onCloseHandler = () => {
				clearPanel();
				clearTaskSelection();
				props.onClose?.();
			};

			const handleComponentClose = () => {
				clearPanel();
				clearTaskSelection();
				if (props.onClose) {
					props.onClose();
				}
			};

			setPanel({
				id: `task-${props.task.id}`,
				component: (
					<TaskDetailsComponent {...props} onClose={handleComponentClose} />
				),
				meta: {
					title: `Tâche #${props.task.id}`,
					onClose: onCloseHandler,
					onBack: onCloseHandler,
				},
			});
		},
		[setPanel, clearPanel, clearTaskSelection, me?.role],
	);

	const showDocumentPreview = useCallback(
		(props: {
			url: string;
			title?: string;
			documentId?: string;
			mimeType?: string;
			fileName?: string;
			onClose?: () => void;
			onDelete?: (documentId: string) => void;
		}) => {
			const onCloseHandler = () => {
				clearPanel();
				clearDocument();
				props.onClose?.();
			};

			const component = (
				<FileWithMetadataPreviewPanel fileId={props.documentId} />
			);

			const title = props.documentId ? (
				<EditableFileName
					fileId={props.documentId}
					name={props.title || "Aperçu du document"}
				/>
			) : (
				props.title || "Aperçu du document"
			);

			setPanel({
				id: `document-${Date.now()}`,
				component,
				meta: {
					title,
					onClose: onCloseHandler,
					onBack: onCloseHandler,
					actions: (
						<FileWithMetadataPreviewPanelActions
							fileId={props.documentId}
							onDelete={props.onDelete}
						/>
					) 
				},
			});
		},
		[setPanel, clearPanel, clearDocument],
	);

	const showCustomContent = useCallback(
		(content: Omit<PanelContent, "id"> & { id?: string }) => {
			setPanel({
				id: content.id || `custom-${Date.now()}`,
				...content,
			});
		},
		[setPanel],
	);

	const markDirty = useCallback(
		(isDirty = true) => {
			updatePanelMeta({ isDirty });
		},
		[updatePanelMeta],
	);

	return useMemo(
		() => ({
			setPanel: showCustomContent,
			showCustomContent,
			clearPanel,
			goBack,
			updatePanelMeta,
			markDirty,

			showEmailComposer,
			showEmailPreview,
			showTaskDetails,
			showDocumentPreview,
		}),
		[
			showCustomContent,
			clearPanel,
			goBack,
			updatePanelMeta,
			markDirty,
			showEmailComposer,
			showEmailPreview,
			showTaskDetails,
			showDocumentPreview,
		],
	);
};
